import React, { FC, useEffect } from "react";
import { useGetPatientQuery, useGetPatientsQuery } from "@store/patientApi";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Loader from "@components/Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import styled from "@emotion/styled";
import { ScreenSize } from "@constants/screen-size";
import { useMediaQuery } from "react-responsive";
import {
  Mode,
  selectPatientId,
  setCompanionMode,
  setPatients,
  setSelectedPatient,
} from "@store/homeSlice";
import HomeLayout from "@components/Home/HomeLayout/HomeLayout";
import PhotosProcessingStatusSnackbar from "@components/PhotosProcessingStatusSnackbar";
import { useReminders } from "@hooks/useReminders";
import AccountSetupProgress from "@components/AccountSetupProgress";
import InviteDialog from "@components/InviteDialog/InviteDialog";
import SessionTimedOutDialog from "@components/SessionTimedOutDialog";

const Page = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  overflow: hidden;

  @media (max-width: ${ScreenSize.md}px) {
    padding: 0;
  }
`;

const HomeRoutes: FC<any> = () => {
  useReminders();
  const isPatient = useSelector(
    (state: RootState) => state.auth.user?.isPatient,
  );
  const { data: patients, isLoading, isError } = useGetPatientsQuery();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({
    query: `(max-width: ${ScreenSize.md}px)`,
  });
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: selectedPatient } = useGetPatientQuery(selectedPatientId!, {
    skip: !selectedPatientId,
  });
  const dispatch = useDispatch();
  const location = useLocation();
  const isPatientView =
    isPatient || location?.pathname?.startsWith("/portal");

  useEffect(() => {
    if (isPatient && !location?.pathname.startsWith("/portal")) {
      navigate("/portal");
    }
    dispatch(
      setCompanionMode(
        isPatient || location?.pathname?.includes("portal")
          ? Mode.Patient
          : Mode.Caregiver,
      ),
    );
  }, [location, dispatch, isPatient, navigate]);

  useEffect(() => {
    // Hide the floating Brevo launcher (bottom-right):
    // - always on mobile form factor (patient + caregiver)
    // - always in patient view on desktop
    //
    // but help remains accessible via the "?" action that calls `showHelp()`.
    if (isMobile || isPatientView) {
      window.Argoli.Brevo.hideHelpButton();

      let mobileHideEnforcer: number | null = null;
      if (isMobile) {
        // On mobile, `showHelp()` may need to show the launcher momentarily to
        // open the panel. Keep re-hiding it only when chat is not expanded, so
        // active chats are not collapsed.
        mobileHideEnforcer = window.setInterval(() => {
          const isChatExpanded =
            window.location.hash === "#brevoConversationsExpanded";
          if (!isChatExpanded) {
            window.Argoli.Brevo.hideHelpButton();
          }
        }, 500);
      }

      return () => {
        if (mobileHideEnforcer !== null) {
          window.clearInterval(mobileHideEnforcer);
        }
        window.Argoli.Brevo.showHelpButton();
      };
    }
    window.Argoli.Brevo.showHelpButton();
    return undefined;
  }, [isMobile, isPatientView]);

  useEffect(() => {
    dispatch(setSelectedPatient(selectedPatient || null));
  }, [selectedPatient, dispatch]);

  useEffect(() => {
    if (isLoading || isError) {
      return;
    }
    if (!patients?.data?.length) {
      navigate("/onboarding/profile");
    } else {
      dispatch(setPatients(patients.data));
      if (!selectedPatientId) {
        dispatch(selectPatientId(patients.data[0].id));
      }
    }
  }, [
    patients,
    isLoading,
    navigate,
    dispatch,
    selectedPatientId,
    isPatient,
    isError,
  ]);

  return isLoading ? (
    <Loader />
  ) : (
    <Page>
      {!isPatient && (
        <>
          <PhotosProcessingStatusSnackbar />
          <AccountSetupProgress />
          <InviteDialog />
        </>
      )}
      <SessionTimedOutDialog />

      <HomeLayout>
        <Main>
          <Outlet />
        </Main>
      </HomeLayout>
    </Page>
  );
};

export default HomeRoutes;
